import { Component, computed, inject, input } from '@angular/core';
import { RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { Button } from 'primeng/button';
import { Card } from 'primeng/card';

import { ModuleRegistryService, ModuleStatus } from '../../services/module-registry.service';

/**
 * Why a module cannot be opened. `unknown` means the id is not configured at
 * all; `stale` means its chunk vanished under a redeploy and a reload fixes it.
 */
export type ModuleUnavailableReason = Exclude<ModuleStatus, 'ready'> | 'unknown' | 'stale';

/**
 * Shown instead of a module that cannot be opened.
 *
 * Reached two ways: by URL, when the id resolves to no route (unknown, disabled
 * or unresolved module), and from inside a module's own route when its
 * `loadRoutes()` rejects. The second case supplies `reason` through route data,
 * because by then the registry still believes the module is fine.
 *
 * The wording is deliberately about the installation rather than the user —
 * whoever lands here can do nothing but go back, while the text is what they
 * will quote to support.
 */
@Component({
  selector: 'app-module-unavailable',
  imports: [Card, Button, RouterLink, TranslatePipe],
  template: `
    <p-card [header]="'module.unavailable.title' | translate">
      <p>{{ explanationKey() | translate }}</p>
      <p>
        <code>{{ moduleId() }}</code>
      </p>

      <p-button routerLink="/" [label]="'module.back' | translate" icon="pi pi-arrow-left" />
    </p-card>
  `,
  styles: `
    :host {
      display: block;
      max-width: 40rem;
      margin-inline: auto;
      padding: 2rem 1.5rem;
    }
  `,
})
export class ModuleUnavailable {
  /** Bound from the `:moduleId` route parameter, or from route data. */
  readonly moduleId = input.required<string>();

  /** Set through route data when the registry's own status is not the reason. */
  readonly reason = input<ModuleUnavailableReason>();

  private readonly registry = inject(ModuleRegistryService);

  protected readonly explanationKey = computed(() => {
    const status = this.registry.modules().find((module) => module.id === this.moduleId())?.status;
    const reason = this.reason() ?? (status === 'ready' || !status ? 'unknown' : status);

    return `module.unavailable.${reason}`;
  });
}
